import { REACT_APP_API_URL } from '../config';
import { apiFetch } from '../App';

/**
 * Gets the user's email from localStorage or from the API if not stored
 * @returns {Promise<string>} The user's email address
 * @throws {Error} If authentication fails
 */
export const getUserEmail = async () => {
  // First try to get from localStorage
  const storedEmail = localStorage.getItem('userEmail');
  if (storedEmail) {
    console.log('Using stored user email:', storedEmail);
    return storedEmail;
  }
  
  // If not available, fetch from API
  console.log('No stored email, fetching from API');
  const token = localStorage.getItem('accessToken');
  if (!token) {
    console.error('No access token available');
    throw new Error('Authentication required. Please log in again.');
  }
  
  try {
    // Prevent multiple calls by setting a temporary flag
    const inProgress = sessionStorage.getItem('fetchingUserEmail');
    if (inProgress === 'true') {
      console.log('Another request to fetch user email is in progress, waiting...');
      // Wait a bit and check if the email has been stored by another request
      await new Promise(resolve => setTimeout(resolve, 500));
      const emailAfterWait = localStorage.getItem('userEmail');
      if (emailAfterWait) {
        console.log('Email was fetched by another request:', emailAfterWait);
        return emailAfterWait;
      }
    }
    
    // Set the in-progress flag
    sessionStorage.setItem('fetchingUserEmail', 'true');
    
    // Use try-finally to ensure the flag is cleared regardless of outcome
    try {
      const userResponse = await apiFetch('current-user');
      if (userResponse.ok) {
        const userData = await userResponse.json();
        const email = userData.email;
        // Store for future use
        localStorage.setItem('userEmail', email);
        console.log('Retrieved and stored user email:', email);
        return email;
      } else {
        const status = userResponse.status;
        const message = await userResponse.text();
        console.error(`Failed to get user information: ${status} ${message}`);
        throw new Error(`Failed to get user information: ${status}`);
      }
    } finally {
      // Always clear the in-progress flag
      sessionStorage.removeItem('fetchingUserEmail');
    }
  } catch (error) {
    console.error('Error getting user email:', error);
    throw error;
  }
};

/**
 * Clears all user-related data from localStorage
 */
export const clearUserData = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userEmail');
  console.log('User data cleared');
};

/**
 * Checks if the user is currently authenticated
 * @returns {boolean} True if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('accessToken');
}; 